/**
 * src/modules/perfil-prode.js
 *
 * Vista "Mi Prode" del perfil del jugador.
 *
 * Pestañas (refactorizadas 28-may-2026):
 *   - Mi Mundial: top 10 mejores predicciones por puntos_obtenidos DESC.
 *   - Análisis: todos los partidos finalizados, agrupados por fase + cards.
 *   - Evolución: tabla cronológica simple (Fecha/Partido/Fase/Puntos/Acumulado).
 *
 * Mi Mundial y Análisis usan EL MISMO componente que el Prode del grupo
 * (renderMatchCard de prode.js) con un predOverride que mapea el row del
 * historial al shape `pred` esperado. Para partidos finished la card ya es
 * read-only por construcción (sin botón JUGAR/EDITAR ni "Ver puntos en juego").
 */

import { activeGroupBarPM, bottomNavPM, formatPM } from './perfil-helpers.js';
import { getMembership } from '../core/groups.js';
import { getHistorialProde } from '../core/predicciones.js';
import { formatFechaART } from '../utils/fecha-art.js';
import { renderMatchCard } from './prode.js';
import { mountMatchCardShadow } from '../ui/match-card.js';

const escapeHtmlPM = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// Mapeo de fase canónica → etiqueta UI. 'final' con es_tercer_puesto=true → "3er Puesto".
const labelFase = (fase, esTercerPuesto) => {
  if (esTercerPuesto) return '3er Puesto';
  switch (fase) {
    case 'grupo': return 'Grupos';
    case '16avos': return '16avos';
    case 'octavos': return 'Octavos';
    case 'cuartos': return 'Cuartos';
    case 'semifinal': return 'Semis';
    case 'final': return 'Final';
    default: return fase || '—';
  }
};

// Orden canónico de fases para Análisis (más adelante del torneo arriba).
const FASES_ORDEN = ['final', 'semifinal', 'cuartos', 'octavos', '16avos', 'grupo'];

// Sub-pestañas de fase dentro de Análisis. Orden natural del torneo (grupos
// arriba, final abajo), inverso de FASES_ORDEN. Reincorporado tras smoke
// 27-may: sin estas tabs todos los finished caen en una lista única.
const FASES_ANALISIS_TABS = [
  { key: 'grupo', label: 'F Grupos' },
  { key: '16avos', label: '16avos' },
  { key: 'octavos', label: 'Octavos' },
  { key: 'cuartos', label: 'Cuartos' },
  { key: 'semifinal', label: 'Semifinal' },
  { key: 'final', label: 'Final' },
];

const analisisFinishedDeFase = (historial, faseKey) =>
  (historial || []).filter((row) => row.partido?.status === 'finished' && (row.partido?.fase || 'grupo') === faseKey);

const analisisHayFinished = (historial) =>
  (historial || []).some((row) => row.partido?.status === 'finished');

const analisisPrimeraFaseConFinished = (historial) => {
  for (const f of FASES_ANALISIS_TABS) {
    if (analisisFinishedDeFase(historial, f.key).length > 0) return f.key;
  }
  return FASES_ANALISIS_TABS[0].key;
};

// String HTML con las cards de finished de UNA fase, sort por fecha DESC.
// Vacío si la fase no tiene finished — el caller monta un mini empty state.
const cardsAnalisisPorFase = (historial, faseKey) => {
  const rows = analisisFinishedDeFase(historial, faseKey);
  if (rows.length === 0) return '';
  const sorted = [...rows].sort((a, b) => {
    const fa = a.partido.fecha_hora ? new Date(a.partido.fecha_hora).getTime() : 0;
    const fb = b.partido.fecha_hora ? new Date(b.partido.fecha_hora).getTime() : 0;
    return fb - fa;
  });
  return sorted.map((row) => renderMatchCard(row.partido, historialToPred(row))).join('');
};

// Label de la predicción del jugador (Evolución y label interno).
//   Grupos: signo ("Gana ARG" / "Empate" / "Gana ALG"). No mostrar "1-0".
//   Playoffs: goles ("2-1") + clasificado por penales si empate ("1-1 (gana ARG)").
export const labelPrediccion = (row, partido) => {
  if (row.score_local == null || row.score_visitante == null) return '—';
  if (row.prediction_type === 'group_match') {
    if (row.score_local > row.score_visitante) return `Gana ${partido.equipo_local_codigo || partido.equipo_local_nombre || 'local'}`;
    if (row.score_local < row.score_visitante) return `Gana ${partido.equipo_visitante_codigo || partido.equipo_visitante_nombre || 'visitante'}`;
    return 'Empate';
  }
  const base = `${row.score_local}-${row.score_visitante}`;
  if (row.score_local === row.score_visitante && row.clasificado_codigo) {
    return `${base} (gana ${row.clasificado_codigo})`;
  }
  return base;
};

// historialToPred(row) — mapea un row del historial al shape `pred` que
// renderMatchCard de prode.js espera (pick / goalsHome / goalsAway /
// penaltyWinner / label / points).
const historialToPred = (row) => {
  if (row.score_local == null || row.score_visitante == null) return null;
  const partido = row.partido || {};
  const points = row.puntos_obtenidos ?? 0;
  if (row.prediction_type === 'group_match') {
    let pick = 'draw';
    if (row.score_local > row.score_visitante) pick = 'home';
    else if (row.score_local < row.score_visitante) pick = 'away';
    return {
      pick,
      label: labelPrediccion(row, partido),
      points,
    };
  }
  // playoff_match
  let penaltyWinner = null;
  if (row.score_local === row.score_visitante && row.clasificado_codigo) {
    if (row.clasificado_codigo === partido.equipo_local_codigo) penaltyWinner = 'home';
    else if (row.clasificado_codigo === partido.equipo_visitante_codigo) penaltyWinner = 'away';
  }
  return {
    goalsHome: row.score_local,
    goalsAway: row.score_visitante,
    penaltyWinner,
    label: labelPrediccion(row, partido),
    points,
  };
};

const emptyState = (titulo, mensaje) => `
  <div class="prode-empty-state">
    <div class="prode-empty-icon">⚽</div>
    <h3>${escapeHtmlPM(titulo)}</h3>
    <p>${escapeHtmlPM(mensaje)}</p>
  </div>
`;

// ─── Mi Mundial: top 10 mejores predicciones (DESC por puntos) ─────────────
export const renderMiMundial = (historial) => {
  const finished = (historial || []).filter((row) =>
    row.partido?.status === 'finished' && row.puntos_obtenidos != null
  );
  if (finished.length === 0) {
    return emptyState(
      'Tu top 10 va a aparecer acá.',
      'Cuando se finalicen partidos, vas a ver tus 10 mejores resultados.'
    );
  }
  const top10 = [...finished].sort((a, b) => b.puntos_obtenidos - a.puntos_obtenidos).slice(0, 10);
  return `<div>${top10.map((row) => renderMatchCard(row.partido, historialToPred(row))).join('')}</div>`;
};

// ─── Análisis: todos los finished agrupados por fase ───────────────────────
export const renderAnalisis = (historial) => {
  const finished = (historial || []).filter((row) => row.partido?.status === 'finished');
  if (finished.length === 0) {
    return emptyState(
      'Tu análisis del Mundial va a aparecer acá.',
      'Cada partido finalizado se agrupa por fase con tu predicción y tus puntos.'
    );
  }
  const porFase = new Map();
  for (const row of finished) {
    const fase = row.partido.fase || 'grupo';
    if (!porFase.has(fase)) porFase.set(fase, []);
    porFase.get(fase).push(row);
  }
  const secciones = FASES_ORDEN
    .filter((fase) => porFase.has(fase))
    .map((fase) => {
      const rows = porFase.get(fase);
      const sorted = [...rows].sort((a, b) => {
        const fa = a.partido.fecha_hora ? new Date(a.partido.fecha_hora).getTime() : 0;
        const fb = b.partido.fecha_hora ? new Date(b.partido.fecha_hora).getTime() : 0;
        return fb - fa;
      });
      const cards = sorted.map((row) => renderMatchCard(row.partido, historialToPred(row))).join('');
      const label = labelFase(fase, false).toUpperCase();
      return `
        <div style="margin-bottom:1.05rem;">
          <h3 style="font-family:var(--font-heading); color:var(--white); font-size:.86rem; text-transform:uppercase; letter-spacing:.06em; margin:0 0 .55rem; padding-bottom:.32rem; border-bottom:1px solid rgba(255,255,255,.08);">${escapeHtmlPM(label)}</h3>
          ${cards}
        </div>
      `;
    });
  return `<div>${secciones.join('')}</div>`;
};

// ─── Evolución: tabla cronológica simple (Fecha/Partido/Fase/Puntos/Acumulado) ───
export const renderEvolucion = (historial) => {
  const finished = (historial || []).filter((row) =>
    row.partido?.status === 'finished' && row.puntos_obtenidos != null
  );
  if (finished.length === 0) {
    return emptyState(
      'Tu evolución va a aparecer acá.',
      'Cuando se finalicen partidos, vas a ver tu camino cronológico.'
    );
  }
  const asc = [...finished].sort((a, b) => {
    const fa = a.partido.fecha_hora ? new Date(a.partido.fecha_hora).getTime() : 0;
    const fb = b.partido.fecha_hora ? new Date(b.partido.fecha_hora).getTime() : 0;
    return fa - fb;
  });
  let acumulado = 0;
  const rowsAsc = asc.map((row) => {
    acumulado += row.puntos_obtenidos;
    return { row, acumulado };
  });
  const rows = [...rowsAsc].reverse().map(({ row, acumulado }) => {
    const partido = row.partido || {};
    const pts = row.puntos_obtenidos;
    const ptsLabel = pts == null ? '—' : (pts > 0 ? `+${pts}` : String(pts));
    const ptsColor = pts == null ? 'var(--text-muted)' : (pts > 0 ? '#22c55e' : (pts < 0 ? '#f87171' : 'var(--text-muted)'));
    const local = escapeHtmlPM(partido.equipo_local_codigo || partido.equipo_local_nombre || '—');
    const visitante = escapeHtmlPM(partido.equipo_visitante_codigo || partido.equipo_visitante_nombre || '—');
    const partidoLabel = `${local} vs ${visitante}`;
    const fase = labelFase(partido.fase, partido.es_tercer_puesto);
    const fecha = partido.fecha_hora ? formatFechaART(partido.fecha_hora) : '—';
    return `
      <tr>
        <td style="padding:.42rem .5rem; font-size:.66rem; color:var(--text-muted); white-space:nowrap;">${fecha}</td>
        <td style="padding:.42rem .5rem; font-size:.74rem; color:var(--white); white-space:nowrap;">${partidoLabel}</td>
        <td style="padding:.42rem .5rem; font-size:.66rem; color:var(--celeste); text-align:center;">${fase}</td>
        <td style="padding:.42rem .5rem; font-size:.74rem; color:${ptsColor}; font-weight:900; white-space:nowrap; text-align:right;">${ptsLabel}</td>
        <td style="padding:.42rem .5rem; font-size:.74rem; color:var(--white); font-weight:900; white-space:nowrap; text-align:right;">${formatPM(acumulado)}</td>
      </tr>
    `;
  }).join('');
  return `
    <div style="overflow-x:auto; border-radius:12px; border:1px solid rgba(255,255,255,.08); background:rgba(255,255,255,.03);">
      <table style="width:100%; border-collapse:collapse;">
        <thead>
          <tr style="background:rgba(255,255,255,.04); border-bottom:1px solid rgba(255,255,255,.08);">
            <th style="padding:.5rem; text-align:left; font-size:.6rem; color:var(--text-muted); text-transform:uppercase; letter-spacing:.04em;">Fecha</th>
            <th style="padding:.5rem; text-align:left; font-size:.6rem; color:var(--text-muted); text-transform:uppercase; letter-spacing:.04em;">Partido</th>
            <th style="padding:.5rem; text-align:center; font-size:.6rem; color:var(--text-muted); text-transform:uppercase; letter-spacing:.04em;">Fase</th>
            <th style="padding:.5rem; text-align:right; font-size:.6rem; color:var(--text-muted); text-transform:uppercase; letter-spacing:.04em;">Puntos</th>
            <th style="padding:.5rem; text-align:right; font-size:.6rem; color:var(--text-muted); text-transform:uppercase; letter-spacing:.04em;">Acumulado</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;
};

// Alias compatibilidad.
export const renderHistorialProde = renderEvolucion;

export const renderPerfilProde = async (codigo) => {
  const root = document.getElementById('app-root');
  if (!root) return;
  const membership = getMembership(codigo);
  const playerId = membership?.id || null;
  const historial = playerId ? await getHistorialProde(playerId) : [];

  const renderTab = (tabKey) => {
    if (tabKey === 'analisis') return renderAnalisis(historial);
    if (tabKey === 'evolucion') return renderEvolucion(historial);
    return renderMiMundial(historial);
  };

  // Las pestañas Mi Mundial y Análisis muestran cards de renderMatchCard que
  // dependen de PRODE_CSS (vive dentro del Shadow DOM del Prode del grupo).
  // Para que se vean igual acá las metemos en un Shadow DOM propio vía
  // mountMatchCardShadow. La pestaña Evolución es una tabla común (CSS de la
  // SPA) y NO usa Shadow DOM. El empty state también vive con el CSS de la
  // SPA (perfil-runtime.css) — se detecta por la marca `prode-empty-state`.
  //
  // Análisis tiene además sub-pestañas internas de fase (F Grupos / 16avos /
  // Octavos / Cuartos / Semifinal / Final). El handler de cada sub-pestaña
  // re-pinta SOLO el host de cards con mountMatchCardShadow, sin tocar el
  // wrapper de sub-pestañas (los listeners siguen vivos).
  const pintarTab = (panel, tabKey) => {
    // Análisis SIEMPRE muestra sub-pestañas de fase, haya o no finished. Si una
    // fase no tiene cards, el mini empty state se ve dentro del host de cards.
    if (tabKey === 'analisis') {
      pintarTabAnalisisConSubtabs(panel, historial);
      return;
    }
    const html = renderTab(tabKey);
    const usaCards = tabKey === 'mundial';
    if (usaCards && !html.includes('prode-empty-state')) {
      panel.innerHTML = '<div data-cards-host></div>';
      const shadow = mountMatchCardShadow(panel.querySelector('[data-cards-host]'), html);
      wireScoringTogglesEnShadow(shadow);
    } else {
      panel.innerHTML = html;
    }
  };

  // Delegación de click [data-score] dentro del Shadow DOM de cards montadas
  // en perfil. El bindEvents del Prode vive en el shadow root del módulo
  // prode.js — desde acá necesitamos nuestro propio listener. Toggle CSS-puro
  // sobre el wrapper que renderMatchCard ya inyectó con display:none.
  //
  // Antes del toggle forzamos `card.open = true`. El botón vive dentro del
  // <summary>, así que con la card cerrada el wrapper (hermano de summary,
  // dentro de match-detail) no se ve aunque el display ya cambió. En Prode
  // refreshMatchCardElement(card, true) garantiza esto vía setAttribute open.
  const wireScoringTogglesEnShadow = (shadow) => {
    if (!shadow) return;
    // Guard: pintarFase reusa el mismo shadowRoot al cambiar de sub-pestaña
    // de Análisis (mountMatchCardShadow no destruye el shadow, solo cambia
    // innerHTML). Sin este flag los listeners se acumulaban y el toggle se
    // cancelaba a sí mismo (par = vuelve al estado original). El listener
    // delegado sigue funcionando aunque el HTML interior se reemplace.
    if (shadow._scoringWired) return;
    shadow._scoringWired = true;
    shadow.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-score]');
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      // Cards grupo en <details.match>; cards playoff en <div.ppc-wrap> sin
      // <details>. Solo abrir si es details (playoff no necesita expansión).
      const card = btn.closest('details.match');
      if (card && !card.open) card.open = true;
      const id = btn.dataset.score;
      const wrapper = shadow.querySelector(`[data-scoring-wrapper="${id}"]`);
      if (!wrapper) return;
      const visible = wrapper.style.display !== 'none';
      wrapper.style.display = visible ? 'none' : '';
      btn.classList.toggle('active', !visible);
    });
  };

  const pintarTabAnalisisConSubtabs = (panel, historial) => {
    const faseInicial = analisisPrimeraFaseConFinished(historial);
    const subtabsHtml = FASES_ANALISIS_TABS.map((f) =>
      `<button class="perfil-tab${f.key === faseInicial ? ' active' : ''}" type="button" data-analisis-fase="${f.key}">${escapeHtmlPM(f.label)}</button>`
    ).join('');
    panel.innerHTML = `
      <div class="perfil-tabs" style="grid-template-columns: repeat(6, minmax(0,1fr)); gap:.2rem; padding:.3rem;">
        ${subtabsHtml}
      </div>
      <div data-cards-host></div>
    `;
    const host = panel.querySelector('[data-cards-host]');
    const pintarFase = (faseKey) => {
      const cards = cardsAnalisisPorFase(historial, faseKey);
      const contenido = cards || '<div style="text-align:center; padding:2rem 1rem; color:#94a3b8; font-size:.85rem; font-weight:800;">Sin partidos finalizados en esta fase todav&iacute;a.</div>';
      const shadow = mountMatchCardShadow(host, contenido);
      wireScoringTogglesEnShadow(shadow);
    };
    pintarFase(faseInicial);
    panel.querySelectorAll('[data-analisis-fase]').forEach((btn) => {
      btn.addEventListener('click', () => {
        panel.querySelectorAll('[data-analisis-fase]').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        pintarFase(btn.dataset.analisisFase);
      });
    });
  };

  root.innerHTML = `
    <div class="perfil-v2" style="margin-bottom:auto;">
      ${activeGroupBarPM(codigo)}
      <header class="perfil-prode-head">
        <button class="perfil-back" type="button" data-prode-back>Volver</button>
        <div>
          <h2 class="perfil-prode-title">Mi Prode Historial</h2>
          <p class="perfil-prode-sub">Tu recorrido personal durante el Mundial</p>
        </div>
      </header>
      <div class="perfil-tabs perfil-tabs-main">
        <button class="perfil-tab active" type="button" data-prode-tab="mundial">Mejores resultados</button>
        <button class="perfil-tab" type="button" data-prode-tab="analisis">An&aacute;lisis</button>
        <button class="perfil-tab" type="button" data-prode-tab="evolucion">Evoluci&oacute;n</button>
      </div>
      <main data-prode-panel></main>
    </div>
    ${bottomNavPM(codigo)}
  `;
  const panel = root.querySelector('[data-prode-panel]');
  pintarTab(panel, 'mundial');
  root.querySelector('[data-prode-back]')?.addEventListener('click', () => {
    window.location.hash = `/perfil/${codigo}`;
  });
  root.querySelectorAll('[data-prode-tab]').forEach((button) => {
    button.addEventListener('click', () => {
      root.querySelectorAll('[data-prode-tab]').forEach((tab) => tab.classList.remove('active'));
      button.classList.add('active');
      pintarTab(panel, button.dataset.prodeTab);
    });
  });
};
